import { forwardRef } from 'react';
import { InputGroup, Input, InputRightElement } from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import { useIntl } from 'react-intl';

import 'react-datepicker/dist/react-datepicker.css';

import { ReactComponent as CalendarIcon } from '../../icons/calendar.svg';

import { enUSFormatExcludingSeconds, createDateAsUTC, stripTimezoneIndicator } from '../../services/items';

const CustomInput = forwardRef((props, ref) => {
    const { disabled } = props;
    return (
        <InputGroup>
            <Input ref={ref} {...props} />
            <InputRightElement opacity={disabled && '0.5'} children={<CalendarIcon />} onClick={props.onClick} />
        </InputGroup>
    );
});

// @workaround The `onBlur` of the DatePicker works correctly after opening and closing at least once.
// `onCalendarClose` works for most cases when you want to apply form validation onBlur
// https://github.com/Hacker0x01/react-datepicker/issues/2028
const Datepicker = forwardRef(
    (
        {
            minDate,
            maxDate,
            value,
            onChange,
            autoComplete = 'off',
            placeholderText,
            dateFormat = enUSFormatExcludingSeconds,
            showTimeInput = true,
            utc = true,
            isClearable = false,
            showMonthYearPicker = false,
            ...rest
        },
        ref
    ) => {
        const intl = useIntl();
        const defaultPlaceholderText = intl.formatMessage({ id: 'common_select_date' });
        const timeInputLabel = intl.formatMessage({ id: 'common_datepicker_time_label' });

        const selected = value ? new Date(utc ? stripTimezoneIndicator(value) : value) : null;

        return (
            <DatePicker
                useWeekdaysShort={true}
                placeholderText={placeholderText ? placeholderText : defaultPlaceholderText}
                timeInputLabel={timeInputLabel}
                autoComplete={autoComplete}
                dateFormat={dateFormat}
                showTimeInput={showTimeInput}
                isClearable={isClearable}
                showMonthYearPicker={showMonthYearPicker}
                onChange={(date) => onChange(utc && date ? createDateAsUTC(date) : date)}
                {...rest}
                ref={ref}
                selected={selected}
                customInput={<CustomInput disabled={rest.disabled} value={selected} />}
                minDate={minDate ? new Date(minDate) : undefined}
                maxDate={maxDate ? new Date(maxDate) : undefined}
            />
        );
    }
);

export default Datepicker;
